export const isInsufficientPermissionsError = (error) => error?.response?.status === 403 && error?.response?.data?.error_code === "INSUFFICIENT_PERMISSIONS";

/**
 * The module is entitled but switched off fleet-wide right now (503). The
 * axios interceptor in lib/api.js already shows the friendly "temporarily
 * unavailable" toast for this case — callers should skip their own, or the
 * user sees that message immediately followed by a second, generic one.
 */
export const isModuleUnavailableError = (error) => error?.response?.status === 503 && error?.response?.data?.error_code === "MODULE_TEMPORARILY_UNAVAILABLE";

/**
 * The tenant is not entitled to this module at all (403). The interceptor
 * shows no toast for this on purpose — a properly gated surface should never
 * make this call, so seeing it means a surface slipped through. Exposed so a
 * caller can still tell the two 403/503 module cases apart if it needs to.
 */
export const isModuleNotEnabledError = (error) => error?.response?.status === 403 && error?.response?.data?.error_code === "MODULE_NOT_ENABLED";

/**
 * The right place to read a backend error's text from, in the order the
 * backend actually fills them: `message` (AppError's field — every module
 * and business-rule error) then `detail` (FastAPI's own default field).
 * Never the raw `Error.message` — for a failed HTTP call that is Axios's own
 * "Request failed with status code NNN", not anything meant for a user.
 *
 * Accepts two different shapes callers actually pass:
 *  - a raw Axios error (has `.response` / `.isAxiosError`) — read the backend
 *    body from `error.response.data`.
 *  - a plain payload object — e.g. a Redux thunk's
 *    `rejectWithValue(error.response.data)` — which already IS the backend
 *    body, so its own `.message`/`.detail` are read directly.
 */
export const getErrorMessage = (error, fallback) => {
  if (typeof error === "string") return error;
  const body = error?.isAxiosError || error?.response ? error.response?.data : error;
  const message = body?.message || body?.detail;
  return typeof message === "string" && message.trim() ? message : fallback;
};
